import { Link, useParams } from "react-router-dom";
import { css, cx } from "styled-system/css";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale/es";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminPageShell } from "@/components/admin/admin-page-shell";
import { ProfessionalActions } from "@/components/admin/professional-actions";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { EmptyState } from "@/components/admin/empty-state";
import { cf } from "@/components/brand/sfturnos-styles";
import type { PlanType } from "@prisma/client";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { computeTrialDaysLeft, formatTrialDaysLeftLabel } from "@/lib/trial-days";
import { useConfirm } from "@/hooks/useConfirm";

type SubscriptionInfo = {
  paymentMethod: string;
  status: string;
  billingCycle: string;
  agendasCount: number;
  currentPeriodStart: string;
  currentPeriodEnd: string | null;
  activatedBy: string | null;
  activatedAt: string | null;
};

type SubscriptionLogRow = {
  id: string;
  previousPlan: string;
  newPlan: string;
  previousCycle: string | null;
  newCycle: string | null;
  extensionDays: number | null;
  previousExpiresAt: string | null;
  newExpiresAt: string | null;
  paymentMethod: string | null;
  changedBy: string | null;
  note: string | null;
  createdAt: string;
};

type Data = {
  prof: {
    id: string;
    name: string;
    slug: string;
    category: string | null;
    timezone: string;
    plan: PlanType;
    isActive: boolean;
    email: string;
    planExpiresAt: string | null;
    trialDaysLeft: number | null;
    pendingPlanPayment: boolean;
    mpMarketplaceFeePercent: number | null;
    subscription: SubscriptionInfo | null;
  };
  subscriptionLogs: SubscriptionLogRow[];
  stats: {
    appointmentsTotal: number;
    appointmentsUpcoming: number;
    servicesCount: number;
    lastAppointmentAt: string | null;
  };
};

const paymentMethodLabel: Record<string, string> = {
  mercadopago: "Mercado Pago",
  transfer: "Transferencia",
  manual: "Manual / cortesía",
};

/** "YYYY-MM-DD" de hoy en la zona horaria del navegador del admin — valor default del date picker. */
function todayDateOnly(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

type LogRow = {
  id: string;
  channel: string;
  type: string;
  recipient: string;
  status: string;
  error: string | null;
  sentAt: string;
  clientName?: string | null;
  clientPhone?: string | null;
  clientEmail?: string | null;
};

type NotifData = { logs: LogRow[]; total: number };

type Tab = "resumen" | "notificaciones" | "market";

type MarketSub = {
  id: string;
  status: string;
  trialStartAt: string | null;
  trialEndsAt: string | null;
  activeFrom: string | null;
  activeTo: string | null;
  grantedBy: string | null;
  grantNote: string | null;
  feature: { id: string; slug: string; name: string; category: string };
};

type MarketFeatureItem = { id: string; slug: string; name: string; category: string };

function statusTone(status: string): "success" | "danger" | "warning" | "neutral" {
  const s = status.toLowerCase();
  if (s.includes("ok") || s.includes("sent") || s.includes("delivered")) return "success";
  if (s.includes("fail") || s.includes("error") || s.includes("bounce")) return "danger";
  if (s.includes("pend") || s.includes("queue")) return "warning";
  return "neutral";
}

function channelLabel(ch: string) {
  const c = ch.toLowerCase();
  if (c.includes("whatsapp")) return { label: "WhatsApp", tone: "success" as const };
  if (c.includes("sms") || c.includes("twilio")) return { label: "SMS", tone: "warning" as const };
  if (c.includes("email") || c.includes("sendgrid")) return { label: "Email", tone: "neutral" as const };
  return { label: ch, tone: "neutral" as const };
}

function typeLabel(t: string) {
  const map: Record<string, string> = {
    BOOKING_NEW: "Confirmación",
    BOOKING_CANCELLED: "Cancelación",
    BOOKING_RESCHEDULED: "Reprogramación",
    REMINDER_24H: "Recordatorio 24h",
    REMINDER_1H: "Recordatorio 1h",
    REMINDER_48H: "Recordatorio 48h",
    WHATSAPP_CLIENT_REPLY: "Respuesta cliente",
  };
  return map[t] ?? t;
}

const PAGE_SIZE = 50;

export function AdminProfessionalDetail() {
  const { id } = useParams<{ id: string }>();
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const [data, setData] = useState<Data | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [platformGlobalFee, setPlatformGlobalFee] = useState<number | null>(null);
  const [mpFeeDraft, setMpFeeDraft] = useState("");
  const [mpFeeMsg, setMpFeeMsg] = useState<string | null>(null);
  const [mpFeeBusy, setMpFeeBusy] = useState(false);
  const [clearPayBusy, setClearPayBusy] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("resumen");

  // Activar por transferencia
  const [transferPlan, setTransferPlan] = useState<"PRO" | "ENTERPRISE">("PRO");
  const [transferCycle, setTransferCycle] = useState<"monthly" | "annual">("monthly");
  const [transferAgendas, setTransferAgendas] = useState("3");
  const [transferPaymentDate, setTransferPaymentDate] = useState(() => todayDateOnly());
  const [transferNote, setTransferNote] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferMsg, setTransferMsg] = useState<string | null>(null);

  // Extender / días gratis
  const [extendDays, setExtendDays] = useState("30");
  const [extendReason, setExtendReason] = useState("");
  const [extendBusy, setExtendBusy] = useState(false);
  const [extendMsg, setExtendMsg] = useState<string | null>(null);

  // Notifications tab state
  const [notifData, setNotifData] = useState<NotifData | null>(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifError, setNotifError] = useState(false);
  const [notifOffset, setNotifOffset] = useState(0);
  const [notifChannel, setNotifChannel] = useState("");
  const [notifStatus, setNotifStatus] = useState("");

  // Market tab state
  const [marketSubs, setMarketSubs] = useState<MarketSub[] | null>(null);
  const [allFeatures, setAllFeatures] = useState<MarketFeatureItem[]>([]);
  const [grantSlug, setGrantSlug] = useState("");
  const [grantDays, setGrantDays] = useState("30");
  const [grantNote, setGrantNote] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantMsg, setGrantMsg] = useState<string | null>(null);
  const [activateBusyId, setActivateBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      const [r, rPlat] = await Promise.all([
        fetchWithSessionSync(`/api/admin/professionals/${id}`, { credentials: "include" }, refresh),
        fetchWithSessionSync(`/api/admin/platform-settings`, { credentials: "include" }, refresh),
      ]);
      if (cancelled) return;
      if (r.status === 404) {
        setNotFound(true);
        return;
      }
      if (r.ok) {
        const d = (await r.json()) as Data;
        setData(d);
        const ov = d.prof.mpMarketplaceFeePercent;
        setMpFeeDraft(ov != null && Number.isFinite(ov) ? String(ov) : "");
      }
      if (rPlat.ok) {
        const pj = (await rPlat.json()) as { marketplaceFeePercent: number };
        setPlatformGlobalFee(pj.marketplaceFeePercent);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, refresh]);

  // Lazy-load notifications when tab opens or filters change
  useEffect(() => {
    if (activeTab !== "notificaciones" || !id) return;
    let cancelled = false;
    setNotifLoading(true);
    setNotifError(false);
    void (async () => {
      const q = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(notifOffset) });
      if (notifChannel) q.set("channel", notifChannel);
      if (notifStatus) q.set("status", notifStatus);
      const r = await fetchWithSessionSync(
        `/api/admin/professionals/${id}/notif-logs?${q.toString()}`,
        { credentials: "include" },
        refresh,
      );
      if (cancelled) return;
      setNotifLoading(false);
      if (!r.ok) {
        setNotifError(true);
        return;
      }
      setNotifData((await r.json()) as NotifData);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, id, refresh, notifOffset, notifChannel, notifStatus]);

  function openNotifTab() {
    setActiveTab("notificaciones");
  }

  // Lazy-load Market subscriptions when Market tab opens
  useEffect(() => {
    if (activeTab !== "market" || !id) return;
    let cancelled = false;
    void (async () => {
      const [rSubs, rFeatures] = await Promise.all([
        fetchWithSessionSync(`/api/market/admin/subscriptions/${id}`, { credentials: "include" }, refresh),
        fetchWithSessionSync("/api/market/admin/features", { credentials: "include" }, refresh),
      ]);
      if (cancelled) return;
      if (rSubs.ok) {
        const d = (await rSubs.json()) as { subscriptions: MarketSub[] };
        setMarketSubs(d.subscriptions);
      }
      if (rFeatures.ok) {
        const d = (await rFeatures.json()) as { features: MarketFeatureItem[] };
        setAllFeatures(d.features);
        if (!grantSlug && d.features.length > 0) setGrantSlug(d.features[0].slug);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, refresh]);

  async function handleGrantTrial() {
    if (!id || !grantSlug) return;
    const days = Number(grantDays);
    if (!Number.isFinite(days) || days < 1) {
      setGrantMsg("Ingresá una cantidad de días válida.");
      return;
    }
    setGrantBusy(true);
    setGrantMsg(null);
    const r = await fetchWithSessionSync(
      "/api/market/admin/grant-trial",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ professionalId: id, featureSlug: grantSlug, days, note: grantNote.trim() || undefined }),
      },
      refresh,
    );
    setGrantBusy(false);
    if (!r.ok) {
      const d = (await r.json()) as { error?: string };
      setGrantMsg(d.error ?? "Error al otorgar prueba.");
      return;
    }
    setGrantMsg("Prueba otorgada correctamente.");
    // Reload subscriptions
    const rSubs = await fetchWithSessionSync(`/api/market/admin/subscriptions/${id}`, { credentials: "include" }, refresh);
    if (rSubs.ok) {
      const d = (await rSubs.json()) as { subscriptions: MarketSub[] };
      setMarketSubs(d.subscriptions);
    }
    setGrantNote("");
    setTimeout(() => setGrantMsg(null), 4000);
  }

  async function handleActivate(subId: string) {
    if (!id) return;
    setActivateBusyId(subId);
    const r = await fetchWithSessionSync(
      `/api/market/admin/subscriptions/${subId}/activate`,
      { method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({}) },
      refresh,
    );
    setActivateBusyId(null);
    if (!r.ok) {
      setGrantMsg("No se pudo dar acceso manual.");
      setTimeout(() => setGrantMsg(null), 4000);
      return;
    }
    const rSubs = await fetchWithSessionSync(`/api/market/admin/subscriptions/${id}`, { credentials: "include" }, refresh);
    if (rSubs.ok) {
      const d = (await rSubs.json()) as { subscriptions: MarketSub[] };
      setMarketSubs(d.subscriptions);
    }
  }

  if (notFound || !id) {
    return (
      <AdminPageShell title="No encontrado" description="La cuenta no existe.">
        <Link to="/admin/professionals" className={css({ color: "blue.700" })}>
          Volver al listado
        </Link>
      </AdminPageShell>
    );
  }

  if (!data) {
    return (
      <AdminPageShell title="Cuenta">
        <LoadingFrame variant="light" minHeight={320} />
      </AdminPageShell>
    );
  }

  const { prof, stats } = data;
  const publicUrl = `${window.location.origin}/book/${prof.slug}`;
  const trialDaysLeft =
    prof.trialDaysLeft ?? computeTrialDaysLeft(prof.plan, prof.planExpiresAt);
  const trialLabel = formatTrialDaysLeftLabel(trialDaysLeft);
  const trialExpired = trialDaysLeft !== null && trialDaysLeft <= 0;
  const trialSoon = trialDaysLeft !== null && trialDaysLeft > 0 && trialDaysLeft <= 5;

  async function saveMpMarketplaceFee() {
    if (!id) return;
    setMpFeeBusy(true);
    setMpFeeMsg(null);
    const t = mpFeeDraft.trim();
    let body: { mpMarketplaceFeePercent: number | null };
    if (t === "") {
      body = { mpMarketplaceFeePercent: null };
    } else {
      const n = Number(t.replace(",", "."));
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        setMpFeeMsg("Ingresá un porcentaje entre 0 y 100, o dejá vacío para usar la comisión global.");
        setMpFeeBusy(false);
        return;
      }
      body = { mpMarketplaceFeePercent: n };
    }
    const r = await fetchWithSessionSync(
      `/api/admin/professionals/${id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      },
      refresh,
    );
    setMpFeeBusy(false);
    if (!r.ok) {
      setMpFeeMsg("No se pudo guardar.");
      return;
    }
    setMpFeeMsg("Listo: comisión actualizada para esta cuenta.");
    const r2 = await fetchWithSessionSync(`/api/admin/professionals/${id}`, { credentials: "include" }, refresh);
    if (r2.ok) setData((await r2.json()) as Data);
  }

  async function clearPendingPayment() {
    if (!id) return;
    if (!prof.pendingPlanPayment) return;
    if (
      !(await confirm(
        "¿Marcar que el pago del plan ya fue gestionado? Dejará de ver el aviso en el panel del usuario.",
      ))
    )
      return;
    setClearPayBusy(true);
    const r = await fetchWithSessionSync(
      `/api/admin/professionals/${id}`,
      { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pendingPlanPayment: false }) },
      refresh,
    );
    setClearPayBusy(false);
    if (r.ok) {
      const r2 = await fetchWithSessionSync(`/api/admin/professionals/${id}`, { credentials: "include" }, refresh);
      if (r2.ok) setData((await r2.json()) as Data);
    }
  }

  async function reloadProf() {
    if (!id) return;
    const r = await fetchWithSessionSync(`/api/admin/professionals/${id}`, { credentials: "include" }, refresh);
    if (r.ok) setData((await r.json()) as Data);
  }

  async function handleActivateTransfer() {
    if (!id) return;
    if (!transferPaymentDate) {
      setTransferMsg("Ingresá la fecha en la que pagó.");
      return;
    }
    if (
      !(await confirm(
        `¿Activar el plan ${transferPlan} por transferencia? El cliente pasa a tener acceso completo de inmediato.`,
      ))
    )
      return;
    setTransferBusy(true);
    setTransferMsg(null);
    const r = await fetchWithSessionSync(
      `/api/admin/professionals/${id}/activate-transfer`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan: transferPlan,
          billingCycle: transferCycle,
          ...(transferPlan === "ENTERPRISE" ? { agendasCount: Number(transferAgendas) || 3 } : {}),
          paymentDate: transferPaymentDate,
          note: transferNote.trim() || undefined,
        }),
      },
      refresh,
    );
    setTransferBusy(false);
    if (!r.ok) {
      const d = (await r.json().catch(() => ({}))) as { error?: string };
      setTransferMsg(d.error ?? "No se pudo activar la suscripción.");
      return;
    }
    setTransferMsg("Suscripción activada por transferencia.");
    setTransferNote("");
    setTransferPaymentDate(todayDateOnly());
    await reloadProf();
    setTimeout(() => setTransferMsg(null), 5000);
  }

  async function handleExtend() {
    if (!id) return;
    const days = Number(extendDays);
    if (!Number.isFinite(days) || days < 1) {
      setExtendMsg("Ingresá una cantidad de días válida.");
      return;
    }
    if (!extendReason.trim()) {
      setExtendMsg("Ingresá un motivo para dejarlo registrado.");
      return;
    }
    setExtendBusy(true);
    setExtendMsg(null);
    const r = await fetchWithSessionSync(
      `/api/admin/professionals/${id}/extend`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ days, reason: extendReason.trim() }),
      },
      refresh,
    );
    setExtendBusy(false);
    if (!r.ok) {
      const d = (await r.json().catch(() => ({}))) as { error?: string };
      setExtendMsg(d.error ?? "No se pudo extender la suscripción.");
      return;
    }
    setExtendMsg("Vencimiento extendido correctamente.");
    setExtendReason("");
    await reloadProf();
    setTimeout(() => setExtendMsg(null), 5000);
  }

  const tabBase = css({
    px: "4",
    py: "2.5",
    fontSize: "sm",
    fontWeight: "medium",
    cursor: "pointer",
    borderBottomWidth: "2px",
    borderColor: "transparent",
    color: "#64748b",
    bg: "transparent",
    borderTopWidth: "0",
    borderLeftWidth: "0",
    borderRightWidth: "0",
    transition: "color 120ms ease, border-color 120ms ease",
    _hover: { color: "#0f172a" },
  });
  const tabActive = css({ borderBottomColor: "#4f46e5", color: "#4f46e5" });

  return (
    <AdminPageShell
      title={prof.name}
      description={`${prof.email} · slug: ${prof.slug}`}
      actions={
        <Link
          to="/admin/planes"
          className={css({
            fontSize: "sm",
            fontWeight: "medium",
            color: "blue.700",
            _hover: { textDecoration: "underline" },
          })}
        >
          ← Volver al listado
        </Link>
      }
    >
      {/* Tab bar */}
      <div className={css({ borderBottomWidth: "1px", borderColor: "#e2e8f0", display: "flex", gap: "0", mb: "2" })}>
        <button
          type="button"
          className={cx(tabBase, activeTab === "resumen" ? tabActive : "")}
          onClick={() => setActiveTab("resumen")}
        >
          Resumen
        </button>
        <button
          type="button"
          className={cx(tabBase, activeTab === "notificaciones" ? tabActive : "")}
          onClick={openNotifTab}
        >
          Notificaciones
        </button>
        <button
          type="button"
          className={cx(tabBase, activeTab === "market" ? tabActive : "")}
          onClick={() => setActiveTab("market")}
        >
          Market
        </button>
      </div>

      {/* ── RESUMEN TAB ── */}
      {activeTab === "resumen" && (
        <>
          <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", md: "repeat(3, 1fr)" }, gap: "4" })}>
            <div className={css({ rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card", p: "4" })}>
              <p className={css({ fontSize: "xs", color: "fg.muted", fontWeight: "medium" })}>Turnos próximos</p>
              <p className={css({ fontSize: "2xl", fontWeight: "bold", mt: "1" })}>{stats.appointmentsUpcoming}</p>
            </div>
            <div className={css({ rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card", p: "4" })}>
              <p className={css({ fontSize: "xs", color: "fg.muted", fontWeight: "medium" })}>Turnos totales</p>
              <p className={css({ fontSize: "2xl", fontWeight: "bold", mt: "1" })}>{stats.appointmentsTotal}</p>
            </div>
            <div className={css({ rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card", p: "4" })}>
              <p className={css({ fontSize: "xs", color: "fg.muted", fontWeight: "medium" })}>Servicios</p>
              <p className={css({ fontSize: "2xl", fontWeight: "bold", mt: "1" })}>{stats.servicesCount}</p>
            </div>
          </div>

          <div
            className={css({
              rounded: "xl",
              borderWidth: "1px",
              borderColor: "border.DEFAULT",
              bg: "bg.card",
              p: "5",
              display: "grid",
              gap: "4",
              gridTemplateColumns: { base: "1fr", lg: "1fr 1fr" },
            })}
          >
            <div>
              <h2 className={css({ fontWeight: "semibold", mb: "3" })}>Cuenta</h2>
              <dl className={css({ fontSize: "sm", display: "grid", gap: "2" })}>
                <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
                  <dt className={css({ color: "fg.muted" })}>Estado</dt>
                  <dd>
                    <Badge tone={prof.isActive ? "success" : "danger"}>{prof.isActive ? "Activo" : "Inactivo"}</Badge>
                  </dd>
                </div>
                <div className={css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" })}>
                  <dt className={css({ color: "fg.muted" })}>Plan</dt>
                  <dd>
                    <Badge tone="neutral">{prof.plan}</Badge>
                  </dd>
                </div>
                <div>
                  <dt className={css({ color: "fg.muted" })}>Vencimiento de plan</dt>
                  <dd>{prof.planExpiresAt ? new Date(prof.planExpiresAt).toLocaleString("es-AR") : "—"}</dd>
                </div>
                {prof.plan !== "FREE" ? (
                  <div className={css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" })}>
                    <dt className={css({ color: "fg.muted" })}>Método de pago</dt>
                    <dd>
                      <Badge tone={prof.subscription?.paymentMethod === "mercadopago" ? "neutral" : "warning"}>
                        {paymentMethodLabel[prof.subscription?.paymentMethod ?? "mercadopago"] ?? prof.subscription?.paymentMethod}
                      </Badge>
                      {prof.subscription?.activatedBy ? (
                        <span className={css({ fontSize: "xs", color: "fg.muted", ml: "2" })}>
                          Activado por {prof.subscription.activatedBy}
                          {prof.subscription.activatedAt
                            ? ` · ${new Date(prof.subscription.activatedAt).toLocaleDateString("es-AR")}`
                            : ""}
                        </span>
                      ) : null}
                    </dd>
                  </div>
                ) : null}
                {prof.subscription && prof.subscription.paymentMethod !== "mercadopago" && prof.subscription.currentPeriodEnd ? (
                  <div>
                    <dt className={css({ color: "fg.muted" })}>Próxima transferencia esperada</dt>
                    <dd>
                      {new Date(prof.subscription.currentPeriodEnd).toLocaleDateString("es-AR")}
                      <span className={css({ fontSize: "xs", color: "fg.muted", ml: "2" })}>
                        (no hay recordatorio automático — esta cuenta no aparece como "trial" a propósito)
                      </span>
                    </dd>
                  </div>
                ) : null}
                <div className={css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" })}>
                  <dt className={css({ color: "fg.muted" })}>Prueba gratuita</dt>
                  <dd>
                    {trialDaysLeft === null ? (
                      "—"
                    ) : (
                      <Badge tone={trialExpired ? "danger" : trialSoon ? "warning" : "neutral"}>{trialLabel}</Badge>
                    )}
                  </dd>
                </div>
                <div className={css({ display: "flex", flexDir: "column", gap: "2", alignItems: "flex-start" })}>
                  <dt className={css({ color: "fg.muted" })}>Pago de plan (Pro / Enterprise)</dt>
                  <dd className={css({ display: "flex", flexDir: "column", gap: "2", alignItems: "flex-start" })}>
                    {prof.pendingPlanPayment ? (
                      <>
                        <Badge tone="warning">Pendiente de regularizar</Badge>
                        <Button type="button" variant="secondary" disabled={clearPayBusy} onClick={() => void clearPendingPayment()}>
                          {clearPayBusy ? "Guardando…" : "Marcar pago como OK"}
                        </Button>
                      </>
                    ) : (
                      "—"
                    )}
                  </dd>
                </div>
                {prof.category ? (
                  <div>
                    <dt className={css({ color: "fg.muted" })}>Rubro</dt>
                    <dd>{prof.category}</dd>
                  </div>
                ) : null}
                <div>
                  <dt className={css({ color: "fg.muted" })}>Zona horaria</dt>
                  <dd className={css({ fontFamily: "mono", fontSize: "xs" })}>{prof.timezone}</dd>
                </div>
                <div>
                  <dt className={css({ color: "fg.muted" })}>Último turno (registrado)</dt>
                  <dd>
                    {stats.lastAppointmentAt ? new Date(stats.lastAppointmentAt).toLocaleString("es-AR") : "—"}
                  </dd>
                </div>
              </dl>
            </div>
            <div>
              <h2 className={css({ fontWeight: "semibold", mb: "3" })}>Página pública</h2>
              <p className={css({ fontSize: "sm", color: "fg.muted", mb: "2" })}>Link de reservas para compartir con clientes.</p>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={css({
                  fontSize: "sm",
                  wordBreak: "break-all",
                  color: "blue.700",
                  _hover: { textDecoration: "underline" },
                })}
              >
                {publicUrl}
              </a>
            </div>
          </div>

          <div className={css({ rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card", p: "5" })}>
            <h2 className={css({ fontWeight: "semibold", mb: "2" })}>Comisión Mercado Pago (esta cuenta)</h2>
            <p className={css({ fontSize: "sm", color: "fg.muted", mb: "4", maxW: "40rem", lineHeight: "1.5" })}>
              Porcentaje de split que retiene la plataforma cuando el profesional cobra con su cuenta MP (OAuth). Si lo dejás vacío,
              aplica la comisión global del panel{" "}
              {platformGlobalFee != null ? (
                <span className={css({ fontWeight: "medium", color: "fg.DEFAULT" })}>(hoy {platformGlobalFee}%)</span>
              ) : null}
              .
            </p>
            <div className={css({ display: "flex", flexWrap: "wrap", gap: "3", alignItems: "flex-end", maxW: "24rem" })}>
              <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm", flex: "1", minW: "140px" })}>
                % solo para esta cuenta
                <Input value={mpFeeDraft} onChange={(e) => setMpFeeDraft(e.target.value)} placeholder="Vacío = global" />
              </label>
              <Button type="button" disabled={mpFeeBusy} onClick={() => void saveMpMarketplaceFee()}>
                {mpFeeBusy ? "Guardando…" : "Guardar"}
              </Button>
            </div>
            {mpFeeMsg ? (
              <p className={css({ fontSize: "sm", mt: "3", color: mpFeeMsg.startsWith("No") ? "red.700" : "green.700" })}>{mpFeeMsg}</p>
            ) : null}
          </div>

          <div className={css({ rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card", p: "5" })}>
            <h2 className={css({ fontWeight: "semibold", mb: "2" })}>Gestión manual de suscripción</h2>
            <p className={css({ fontSize: "sm", color: "fg.muted", mb: "4", maxW: "42rem", lineHeight: "1.5" })}>
              Para cuando el cliente paga por transferencia en vez de Mercado Pago, o cuando le das días de cortesía/soporte. Ambas
              acciones quedan registradas abajo con quién y cuándo las hizo.
            </p>

            <div className={css({ display: "grid", gap: "5", gridTemplateColumns: { base: "1fr", md: "1fr 1fr" } })}>
              {/* Activar por transferencia */}
              <div className={css({ p: "4", rounded: "lg", borderWidth: "1px", borderColor: "border.DEFAULT" })}>
                <h3 className={css({ fontSize: "sm", fontWeight: "semibold", mb: "3" })}>Activar por transferencia</h3>
                <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
                  <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
                    Plan
                    <select
                      value={transferPlan}
                      onChange={(e) => setTransferPlan(e.target.value as "PRO" | "ENTERPRISE")}
                      className={css({ h: "10", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", fontSize: "sm" })}
                    >
                      <option value="PRO">Pro</option>
                      <option value="ENTERPRISE">Enterprise</option>
                    </select>
                  </label>
                  <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
                    Ciclo
                    <select
                      value={transferCycle}
                      onChange={(e) => setTransferCycle(e.target.value as "monthly" | "annual")}
                      className={css({ h: "10", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", fontSize: "sm" })}
                    >
                      <option value="monthly">Mensual (+30 días)</option>
                      <option value="annual">Anual (+365 días)</option>
                    </select>
                  </label>
                  <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
                    Fecha de pago
                    <Input
                      type="date"
                      value={transferPaymentDate}
                      max={todayDateOnly()}
                      onChange={(e) => setTransferPaymentDate(e.target.value)}
                    />
                  </label>
                  {transferPlan === "ENTERPRISE" ? (
                    <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
                      Cantidad de agendas
                      <Input value={transferAgendas} onChange={(e) => setTransferAgendas(e.target.value)} placeholder="3" />
                    </label>
                  ) : null}
                  <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
                    Nota (opcional)
                    <Input
                      value={transferNote}
                      onChange={(e) => setTransferNote(e.target.value)}
                      placeholder="Ej: transferencia recibida 05/09"
                    />
                  </label>
                  <Button type="button" disabled={transferBusy} onClick={() => void handleActivateTransfer()}>
                    {transferBusy ? "Activando…" : "Activar por transferencia"}
                  </Button>
                  {transferMsg ? (
                    <p className={css({ fontSize: "sm", color: transferMsg.startsWith("No") ? "red.700" : "green.700" })}>
                      {transferMsg}
                    </p>
                  ) : null}
                </div>
              </div>

              {/* Extender / días gratis */}
              <div className={css({ p: "4", rounded: "lg", borderWidth: "1px", borderColor: "border.DEFAULT" })}>
                <h3 className={css({ fontSize: "sm", fontWeight: "semibold", mb: "3" })}>Extender / días gratis</h3>
                <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
                  <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
                    Días a sumar
                    <Input value={extendDays} onChange={(e) => setExtendDays(e.target.value)} placeholder="30" />
                  </label>
                  <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
                    Motivo
                    <Input
                      value={extendReason}
                      onChange={(e) => setExtendReason(e.target.value)}
                      placeholder="Ej: soporte por caída del servicio"
                    />
                  </label>
                  <p className={css({ fontSize: "xs", color: "fg.muted" })}>
                    Extiende desde el vencimiento actual si todavía no venció, o desde hoy si ya venció.
                  </p>
                  <Button type="button" variant="secondary" disabled={extendBusy} onClick={() => void handleExtend()}>
                    {extendBusy ? "Extendiendo…" : "Extender vencimiento"}
                  </Button>
                  {extendMsg ? (
                    <p className={css({ fontSize: "sm", color: extendMsg.startsWith("No") || extendMsg.startsWith("Ingresá") ? "red.700" : "green.700" })}>
                      {extendMsg}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Historial */}
            {data.subscriptionLogs.length > 0 ? (
              <div className={css({ mt: "5" })}>
                <h3 className={css({ fontSize: "sm", fontWeight: "semibold", mb: "2" })}>Historial de cambios</h3>
                <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
                  {data.subscriptionLogs.map((log) => (
                    <div
                      key={log.id}
                      className={css({ fontSize: "sm", p: "3", rounded: "md", bg: "#f8fafc", display: "flex", flexDir: "column", gap: "0.5" })}
                    >
                      <div className={css({ display: "flex", justifyContent: "space-between", gap: "3", flexWrap: "wrap" })}>
                        <span className={css({ fontWeight: "medium" })}>
                          {log.extensionDays != null
                            ? `+${log.extensionDays} día${log.extensionDays === 1 ? "" : "s"} de extensión`
                            : log.paymentMethod
                              ? `Activado por ${paymentMethodLabel[log.paymentMethod] ?? log.paymentMethod}`
                              : log.previousPlan !== log.newPlan
                                ? `Plan: ${log.previousPlan} → ${log.newPlan}`
                                : "Cambio de suscripción"}
                        </span>
                        <span className={css({ color: "fg.muted", fontSize: "xs" })}>
                          {new Date(log.createdAt).toLocaleString("es-AR")}
                        </span>
                      </div>
                      {log.note ? <p className={css({ color: "fg.muted" })}>{log.note}</p> : null}
                      {log.changedBy ? (
                        <p className={css({ color: "fg.muted", fontSize: "xs" })}>Por {log.changedBy}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className={css({ rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card", p: "5" })}>
            <h2 className={css({ fontWeight: "semibold", mb: "4" })}>Acciones de superadmin</h2>
            <ProfessionalActions
              professionalId={prof.id}
              isActive={prof.isActive}
              plan={prof.plan}
              trialDaysLeft={trialDaysLeft}
              planExpiresAt={prof.planExpiresAt}
            />
          </div>
        </>
      )}

      {/* ── NOTIFICACIONES TAB ── */}
      {activeTab === "notificaciones" && (
        <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
          {/* Filters */}
          <div
            className={cx(
              cf.dashSurface,
              css({
                p: "3",
                display: "flex",
                flexWrap: "wrap",
                gap: "2",
                alignItems: "center",
              }),
            )}
          >
            <select
              value={notifChannel}
              onChange={(e) => {
                setNotifOffset(0);
                setNotifChannel(e.target.value);
              }}
              className={css({
                h: "9",
                px: "3",
                pr: "8",
                rounded: "lg",
                borderWidth: "1px",
                borderColor: "#dbe2ea",
                bg: "#f8fafc",
                fontSize: "sm",
                color: "#0f172a",
                cursor: "pointer",
                _focus: { outline: "none", borderColor: "#4f46e5", boxShadow: "0 0 0 2px rgba(79,70,229,0.15)" },
              })}
            >
              <option value="">Todos los canales</option>
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="sms">SMS</option>
            </select>
            <select
              value={notifStatus}
              onChange={(e) => {
                setNotifOffset(0);
                setNotifStatus(e.target.value);
              }}
              className={css({
                h: "9",
                px: "3",
                pr: "8",
                rounded: "lg",
                borderWidth: "1px",
                borderColor: "#dbe2ea",
                bg: "#f8fafc",
                fontSize: "sm",
                color: "#0f172a",
                cursor: "pointer",
                _focus: { outline: "none", borderColor: "#4f46e5", boxShadow: "0 0 0 2px rgba(79,70,229,0.15)" },
              })}
            >
              <option value="">Todos los estados</option>
              <option value="sent">Enviado</option>
              <option value="failed">Fallido</option>
              <option value="pending">Pendiente</option>
            </select>
            {(notifChannel || notifStatus) && (
              <button
                type="button"
                onClick={() => {
                  setNotifChannel("");
                  setNotifStatus("");
                  setNotifOffset(0);
                }}
                className={css({
                  h: "9",
                  px: "3",
                  rounded: "lg",
                  borderWidth: "1px",
                  borderColor: "#dbe2ea",
                  bg: "white",
                  fontSize: "xs",
                  fontWeight: "medium",
                  color: "#64748b",
                  cursor: "pointer",
                  _hover: { bg: "#f8fafc" },
                })}
              >
                Limpiar
              </button>
            )}
            {notifData && (
              <span className={css({ fontSize: "xs", color: "#64748b", ml: "auto" })}>
                {notifData.total} {notifData.total === 1 ? "envío" : "envíos"}
              </span>
            )}
          </div>

          {/* Table */}
          {notifLoading ? (
            <LoadingFrame variant="light" minHeight={240} />
          ) : notifError ? (
            <div className={cx(cf.dashSurface, css({ p: "6", textAlign: "center" }))}>
              <p className={css({ fontSize: "sm", color: "red.700" })}>No se pudo cargar el historial. Intenta de nuevo.</p>
            </div>
          ) : notifData?.logs.length === 0 ? (
            <EmptyState
              title="Sin notificaciones registradas"
              description="Cuando el sistema envíe emails, WhatsApp o SMS para turnos de esta cuenta, cada intento aparecerá aquí."
              icon="📨"
            />
          ) : notifData ? (
            <div className={cx(cf.dashSurface, css({ overflow: "hidden" }))}>
              <div className={css({ overflowX: "auto" })}>
                <table className={css({ w: "full", borderCollapse: "collapse", fontSize: "sm" })}>
                  <thead>
                    <tr className={css({ bg: "#f8fafc", textAlign: "left" })}>
                      <th className={css({ py: "3", px: "4", fontWeight: "semibold", fontSize: "xs", color: "#64748b", whiteSpace: "nowrap", letterSpacing: "0.02em" })}>
                        Fecha y hora
                      </th>
                      <th className={css({ py: "3", px: "4", fontWeight: "semibold", fontSize: "xs", color: "#64748b", letterSpacing: "0.02em" })}>
                        Canal
                      </th>
                      <th className={css({ py: "3", px: "4", fontWeight: "semibold", fontSize: "xs", color: "#64748b", letterSpacing: "0.02em" })}>
                        Tipo
                      </th>
                      <th className={css({ py: "3", px: "4", fontWeight: "semibold", fontSize: "xs", color: "#64748b", letterSpacing: "0.02em", display: { base: "none", lg: "table-cell" } })}>
                        Cliente
                      </th>
                      <th className={css({ py: "3", px: "4", fontWeight: "semibold", fontSize: "xs", color: "#64748b", letterSpacing: "0.02em" })}>
                        Destinatario
                      </th>
                      <th className={css({ py: "3", px: "4", fontWeight: "semibold", fontSize: "xs", color: "#64748b", letterSpacing: "0.02em" })}>
                        Estado
                      </th>
                      <th className={css({ py: "3", px: "4", fontWeight: "semibold", fontSize: "xs", color: "#64748b", letterSpacing: "0.02em", display: { base: "none", xl: "table-cell" } })}>
                        Error
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {notifData.logs.map((l) => {
                      const ch = channelLabel(l.channel);
                      return (
                        <tr
                          key={l.id}
                          className={css({ borderTopWidth: "1px", borderColor: "#f1f5f9", _hover: { bg: "#fafafa" }, transition: "background 100ms" })}
                        >
                          <td className={css({ py: "3", px: "4", color: "#64748b", whiteSpace: "nowrap", fontSize: "xs", fontFamily: "mono" })}>
                            {format(new Date(l.sentAt), "d MMM yyyy HH:mm", { locale: es })}
                          </td>
                          <td className={css({ py: "3", px: "4" })}>
                            <Badge tone={ch.tone}>{ch.label}</Badge>
                          </td>
                          <td className={css({ py: "3", px: "4", fontSize: "xs", color: "#334155", fontWeight: "medium" })}>
                            {typeLabel(l.type)}
                          </td>
                          <td className={css({ py: "3", px: "4", fontSize: "xs", display: { base: "none", lg: "table-cell" } })}>
                            {l.clientName ? (
                              <span className={css({ fontWeight: "medium", color: "#0f172a" })}>{l.clientName}</span>
                            ) : (
                              <span className={css({ color: "#94a3b8" })}>—</span>
                            )}
                          </td>
                          <td className={css({ py: "3", px: "4", maxW: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "xs", color: "#475569", fontFamily: "mono" })}>
                            {l.recipient}
                          </td>
                          <td className={css({ py: "3", px: "4" })}>
                            <Badge tone={statusTone(l.status)}>{l.status}</Badge>
                          </td>
                          <td className={css({ py: "3", px: "4", fontSize: "xs", color: "red.700", maxW: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: { base: "none", xl: "table-cell" } })}>
                            {l.error ? (
                              <span title={l.error}>{l.error}</span>
                            ) : (
                              <span className={css({ color: "#94a3b8" })}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {notifData.total > PAGE_SIZE && (
                <div
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: "4",
                    py: "3",
                    borderTopWidth: "1px",
                    borderColor: "#f1f5f9",
                  })}
                >
                  <span className={css({ fontSize: "xs", color: "#64748b" })}>
                    {notifOffset + 1}–{Math.min(notifOffset + PAGE_SIZE, notifData.total)} de {notifData.total}
                  </span>
                  <div className={css({ display: "flex", gap: "2" })}>
                    <button
                      type="button"
                      disabled={notifOffset === 0}
                      onClick={() => setNotifOffset((o) => Math.max(0, o - PAGE_SIZE))}
                      className={css({
                        h: "8",
                        px: "3",
                        rounded: "md",
                        borderWidth: "1px",
                        borderColor: "#dbe2ea",
                        bg: "white",
                        fontSize: "xs",
                        fontWeight: "medium",
                        cursor: "pointer",
                        _disabled: { opacity: 0.4, cursor: "not-allowed" },
                        _hover: { bg: "#f8fafc" },
                      })}
                    >
                      Anterior
                    </button>
                    <button
                      type="button"
                      disabled={notifOffset + PAGE_SIZE >= notifData.total}
                      onClick={() => setNotifOffset((o) => o + PAGE_SIZE)}
                      className={css({
                        h: "8",
                        px: "3",
                        rounded: "md",
                        borderWidth: "1px",
                        borderColor: "#dbe2ea",
                        bg: "white",
                        fontSize: "xs",
                        fontWeight: "medium",
                        cursor: "pointer",
                        _disabled: { opacity: 0.4, cursor: "not-allowed" },
                        _hover: { bg: "#f8fafc" },
                      })}
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* ── MARKET TAB ── */}
      {activeTab === "market" && (
        <div className={css({ display: "flex", flexDir: "column", gap: "6" })}>
          {/* Grant trial form */}
          <div
            className={css({
              rounded: "xl",
              borderWidth: "1px",
              borderColor: "#e2e8f0",
              bg: "white",
              p: "5",
              display: "flex",
              flexDir: "column",
              gap: "4",
            })}
          >
            <h2 className={css({ fontSize: "sm", fontWeight: "bold", color: "#0f172a" })}>
              Otorgar período de prueba
            </h2>

            <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", md: "1fr 1fr 1fr" }, gap: "3", alignItems: "flex-end" })}>
              <div>
                <label className={css({ display: "block", fontSize: "xs", fontWeight: "medium", color: "#374151", mb: "1.5" })}>
                  Feature
                </label>
                <select
                  value={grantSlug}
                  onChange={(e) => setGrantSlug(e.target.value)}
                  className={css({
                    w: "full",
                    fontSize: "sm",
                    color: "#0f172a",
                    bg: "white",
                    borderWidth: "1px",
                    borderColor: "#e2e8f0",
                    rounded: "lg",
                    px: "3",
                    py: "2",
                    outline: "none",
                    cursor: "pointer",
                  })}
                >
                  {allFeatures.map((f) => (
                    <option key={f.slug} value={f.slug}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={css({ display: "block", fontSize: "xs", fontWeight: "medium", color: "#374151", mb: "1.5" })}>
                  Días de prueba
                </label>
                <Input
                  type="number"
                  min={1}
                  value={grantDays}
                  onChange={(e) => setGrantDays(e.target.value)}
                  placeholder="30"
                />
              </div>
              <div>
                <label className={css({ display: "block", fontSize: "xs", fontWeight: "medium", color: "#374151", mb: "1.5" })}>
                  Nota interna (opcional)
                </label>
                <Input
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  placeholder="Pilot cliente VIP…"
                />
              </div>
            </div>

            <div className={css({ display: "flex", gap: "3", alignItems: "center" })}>
              <Button size="sm" onClick={() => void handleGrantTrial()} disabled={grantBusy || allFeatures.length === 0}>
                {grantBusy ? "Otorgando…" : "Otorgar prueba"}
              </Button>
              {grantMsg && (
                <span
                  className={css({
                    fontSize: "sm",
                    color: grantMsg.startsWith("Error") || grantMsg.startsWith("Ingresá") ? "#dc2626" : "#15803d",
                  })}
                >
                  {grantMsg}
                </span>
              )}
            </div>
          </div>

          {/* Subscriptions list */}
          <div
            className={css({
              rounded: "xl",
              borderWidth: "1px",
              borderColor: "#e2e8f0",
              bg: "white",
              overflow: "hidden",
            })}
          >
            <div className={css({ px: "5", py: "4", borderBottomWidth: "1px", borderColor: "#f1f5f9" })}>
              <h2 className={css({ fontSize: "sm", fontWeight: "bold", color: "#0f172a" })}>
                Suscripciones activas
              </h2>
            </div>

            {marketSubs === null ? (
              <div className={css({ p: "6" })}>
                <LoadingFrame variant="light" minHeight={80} />
              </div>
            ) : marketSubs.length === 0 ? (
              <p className={css({ px: "5", py: "6", fontSize: "sm", color: "#94a3b8" })}>
                No hay suscripciones para esta agenda.
              </p>
            ) : (
              marketSubs.map((sub, i) => {
                const now = new Date();
                let effectiveStatus = sub.status;
                let daysLeft: number | null = null;
                if (sub.status === "TRIAL" && sub.trialEndsAt) {
                  const ms = new Date(sub.trialEndsAt).getTime() - now.getTime();
                  if (ms <= 0) effectiveStatus = "EXPIRED";
                  else daysLeft = Math.ceil(ms / (1000 * 60 * 60 * 24));
                }
                if (sub.status === "ACTIVE" && sub.activeTo && new Date(sub.activeTo) < now) {
                  effectiveStatus = "EXPIRED";
                }

                // La fecha de vencimiento relevante depende del status actual — un TRIAL vencido
                // muestra trialEndsAt, un ACTIVE vencido/vigente muestra activeTo. Mostrar
                // trialEndsAt para un módulo ACTIVE (como hacía antes) es engañoso: esa fecha queda
                // obsoleta en cuanto se activa manualmente y ya no gobierna el acceso.
                const relevantExpiry =
                  sub.status === "TRIAL" ? sub.trialEndsAt : sub.status === "ACTIVE" ? sub.activeTo : null;
                const metaParts: string[] = [];
                if (relevantExpiry) {
                  metaParts.push(`Vence: ${format(new Date(relevantExpiry), "d MMM yyyy", { locale: es })}`);
                } else if (sub.status === "ACTIVE") {
                  metaParts.push("Acceso permanente");
                }
                if (sub.grantedBy) metaParts.push(`por ${sub.grantedBy}`);
                if (sub.grantNote) metaParts.push(`"${sub.grantNote}"`);

                return (
                  <div
                    key={sub.id}
                    className={css({
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: "4",
                      px: "5",
                      py: "4",
                      borderTopWidth: i > 0 ? "1px" : "0",
                      borderColor: "#f1f5f9",
                    })}
                  >
                    <div className={css({ flex: 1, minW: 0 })}>
                      <div className={css({ display: "flex", alignItems: "center", gap: "2", flexWrap: "wrap" })}>
                        <span className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
                          {sub.feature.name}
                        </span>
                        <span
                          className={css({
                            fontSize: "xs",
                            fontWeight: "medium",
                            px: "2",
                            py: "0.5",
                            rounded: "full",
                            bg: effectiveStatus === "ACTIVE" ? "#dcfce7" : effectiveStatus === "TRIAL" ? "#fef3c7" : "#f1f5f9",
                            color: effectiveStatus === "ACTIVE" ? "#14532d" : effectiveStatus === "TRIAL" ? "#92400e" : "#64748b",
                          })}
                        >
                          {effectiveStatus === "TRIAL"
                            ? daysLeft != null ? `Prueba · ${daysLeft}d` : "En prueba"
                            : effectiveStatus}
                        </span>
                      </div>
                      {metaParts.length > 0 && (
                        <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "1" })}>{metaParts.join(" · ")}</p>
                      )}
                    </div>
                    {effectiveStatus !== "ACTIVE" && (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={activateBusyId === sub.id}
                        onClick={() => void handleActivate(sub.id)}
                      >
                        {activateBusyId === sub.id ? "Activando…" : "Dar acceso manual"}
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </AdminPageShell>
  );
}
